import { HouseBookingTypeEnum, isTravelTypeWithHousing } from '@repo/utils'
import { PlusIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import CreateOrUpdateQuoteProvisionModal, {
  IQuoteProvisionInputProps,
} from './create-or-update-quote-provision-modal/CreateOrUpdateQuoteProvisionModal'
import { ExtractQuoteItemsButton } from './extract-quote-items/ExtractQuoteItemsButton'
import QuoteSectionBodyTablePlanning from './quote-section-body/quote-section-body-table/quote-section-body-table-planning/QuoteSectionBodyTablePlanning'
import QuoteSectionBodyTableSummary from './quote-section-body/quote-section-body-table/quote-section-body-table-summary/QuoteSectionBodyTableSummary'
import QuoteSectionBodyTotalPrice from './quote-section-body/quote-section-body-total-price/QuoteSectionBodyTotalPrice'
import UpdateQuoteTotalPrice from './quote-section-footer/UpdateQuoteTotalPrice'
import { getTableRowActions } from './utils/get-table-row-actions'
import { getEmptyStateData } from './utils/utils'
import { useLocale } from '../../../../../../hooks/use-locale'
import {
  ClientProposalOwnerActionEnum,
  QuoteSectionClientRequestFragment,
} from '../../../../../../lib/graphql'
import { Button } from '../../../../../../shadcn/components/ui/button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../../../../shadcn/components/ui/card'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../../../../shadcn/components/ui/tabs'
import { percentage } from '../../../../../../utils'
import { isNumber } from '../../../../../../utils/format-numbers'
import { CLIENT_PROPOSAL_SERVICE_PROVIDER_OWNER_FEES_RATE } from '../../../../../../utils/prices'
import Toggle from '../../../../../atoms/toggle/Toggle'
import {
  EnvironmentTypeEnum,
  useEnvironmentTypeContext,
  useTravelTypeContext,
  VAT_STATUS,
} from '../../../../../contexts'
import ToggleVatStatus from '../../../../../contexts/vat-status-context/ToggleVatStatus'
import { VatStatusProvider } from '../../../../../contexts/vat-status-context/VatStatusProvider'
import { DownloadPdfDropdown } from '../../../../../organisms/download-pdf-dropdown/DownloadPdfDropdown'
import { IProposalInteractionsProps } from '../../../../../organisms/proposal/Proposal'
import QuoteEmptyState from '../../../../../organisms/proposal/quote-empty-state/QuoteEmptyState'
import UploadQuotationModal, {
  IUploadQuotationProps,
} from '../../../../client-request-cart/client-request-company-information/footer/owner-current-action/upload-quotation-modal/UploadQuotationModal'

interface IQuoteSectionProps {
  clientRequest: QuoteSectionClientRequestFragment
  ownerActions: IProposalInteractionsProps['ownerActions']
  uploadQuotationData?: IUploadQuotationProps
}

const QuoteSection = ({
  clientRequest,
  ownerActions,
  uploadQuotationData,
}: IQuoteSectionProps) => {
  const intl = useIntl()
  const { locale } = useLocale()
  const [
    isOpenCreateOrUpdateQuoteProvisionModal,
    setIsOpenCreateOrUpdateQuoteProvisionModal,
  ] = useState(false)
  const [isOpenLoadQuotation, setIsOpenLoadQuotation] = useState(false)
  const environment = useEnvironmentTypeContext()
  const isBackOffice = environment === EnvironmentTypeEnum.BACKOFFICE

  const [selectedClientPricingItem, setSelectedClientPricingItem] =
    useState<IQuoteProvisionInputProps>()
  const [isSynthesis, setIsSynthesis] = useState<boolean>(true)

  const { travelType } = useTravelTypeContext()

  const clientProposal = clientRequest.clientProposal
  const planningItems = clientRequest.clientProposal?.planning ?? []

  const ownerConfirmedQuotation = ownerActions?.ownerConfirmedQuotation
  const ownerUploadedQuotation = ownerActions?.ownerUploadedQuotation

  if (
    !clientProposal ||
    !clientProposal.price ||
    !clientProposal.clientPricingItems ||
    !clientProposal.ownerCurrentAction ||
    (clientProposal.house
      ? !isNumber(clientProposal.house.fees?.ownerFeesRate)
      : false) ||
    !ownerConfirmedQuotation ||
    !ownerUploadedQuotation
  ) {
    return null
  }

  const currencyArea =
    clientProposal.house?.currencyArea ??
    clientProposal.serviceProvider?.currencyArea ??
    null

  if (!currencyArea) {
    return null
  }

  const { ownerCurrentAction, clientPricingItems, price } = clientProposal

  const adjustmentPricingItems = clientProposal.adjustmentPrices
  const clientPricingItemsToDisplay =
    (isSynthesis ? clientPricingItems : adjustmentPricingItems) ?? []

  const ownerFeesRate =
    clientProposal.house?.fees?.ownerFeesRate ??
    CLIENT_PROPOSAL_SERVICE_PROVIDER_OWNER_FEES_RATE
  const ownerFees = percentage({ rate: ownerFeesRate, locale })
  const emptyStateData = getEmptyStateData({
    ownerCurrentAction,
    intl,
    setIsOpenCreateOrUpdateQuoteProvisionModal,
    setIsOpenLoadQuotation,
  })

  useEffect(() => {
    if (!isOpenCreateOrUpdateQuoteProvisionModal) {
      setSelectedClientPricingItem(undefined)
    }
  }, [isOpenCreateOrUpdateQuoteProvisionModal])

  const displayEmptyState =
    (!isBackOffice &&
      ownerCurrentAction ===
        ClientProposalOwnerActionEnum.CAN_RE_UPLOAD_QUOTATION) ||
    ownerCurrentAction ===
      ClientProposalOwnerActionEnum.BOOKING_DECLINED_BY_HOST

  const canUpdateQuote =
    ownerCurrentAction ===
      ClientProposalOwnerActionEnum.CAN_CONFIRM_QUOTATION ||
    ownerCurrentAction === ClientProposalOwnerActionEnum.CAN_UPLOAD_QUOTATION ||
    (isBackOffice &&
      clientProposal.userAskedQuotationAt &&
      !clientProposal.userConfirmedBookingAt)

  const tableRowActions = getTableRowActions({
    onOpenChange: setIsOpenCreateOrUpdateQuoteProvisionModal,
    clientProposalId: clientProposal._id,
    clientPricingItems,
    setSelectedClientPricingItem,
    currencyArea,
    ownerActions,
    canUpdateQuote,
    intl,
  })

  const displayQuote = !displayEmptyState

  const displayPlanningTab = planningItems.some((item) => item.date !== null)

  const shouldDisplayExtractPdfButton =
    ownerCurrentAction ===
      ClientProposalOwnerActionEnum.CAN_RE_UPLOAD_QUOTATION &&
    isBackOffice &&
    clientProposal.houseBookingType === HouseBookingTypeEnum.QUOTATION

  const shouldDisplayPdfDropdown =
    (clientProposal.quotationConfirmedAt || shouldDisplayExtractPdfButton) &&
    isTravelTypeWithHousing(travelType)

  return (
    <VatStatusProvider initialVatStatus={VAT_STATUS.HT}>
      <Card className='w-full border-grey-400'>
        <CardHeader className='flex flex-row justify-between border-b border-b-grey-400 p-[16px]'>
          <div className='flex flex-col'>
            <CardTitle className='text-xl font-semibold'>
              <FormattedMessage
                id='ui.QuoteSection.myQuote'
                defaultMessage='Mon devis'
              />
            </CardTitle>
            {displayEmptyState && (
              <p className='text-grey-700'>
                <FormattedMessage
                  id='ui.QuoteSection.nabooOwnerFees'
                  defaultMessage='Commission Naboo : {ownerFees}'
                  values={{ ownerFees }}
                />
              </p>
            )}
          </div>

          {!displayEmptyState && (
            <div className='flex items-center gap-4'>
              <ToggleVatStatus />

              {canUpdateQuote && (
                <>
                  {shouldDisplayExtractPdfButton ? (
                    <ExtractQuoteItemsButton
                      clientProposalId={clientProposal._id}
                      hasQuoteItems={clientPricingItems.length > 0}
                      ownerActions={ownerActions}
                    />
                  ) : null}

                  <Button
                    variant='outline'
                    className='flex gap-2'
                    onClick={() =>
                      setIsOpenCreateOrUpdateQuoteProvisionModal(true)
                    }
                  >
                    <PlusIcon size={16} />
                    <FormattedMessage
                      id='ui.quoteSection.header.addAService'
                      defaultMessage='Ajouter une prestation'
                    />
                  </Button>
                </>
              )}
            </div>
          )}
        </CardHeader>

        <CardContent className='h-[500px] p-0'>
          {displayQuote && clientPricingItems.length > 0 ? (
            <Tabs defaultValue='summary' className='flex h-full flex-col'>
              <TabsList className='flex h-fit items-center justify-around rounded-none border-b border-grey-400 bg-white p-0'>
                <TabsTrigger
                  value='summary'
                  className='w-full rounded-none border-0 border-solid border-black py-4 text-grey-600 data-[state=active]:border-b data-[state=active]:text-grey-800 data-[state=active]:shadow-none'
                >
                  <FormattedMessage
                    id='ui.quoteSection.content.tab.summary'
                    defaultMessage='Synthèse des prestations'
                  />
                </TabsTrigger>
                {displayPlanningTab && (
                  <TabsTrigger
                    value='planning'
                    className='w-full rounded-none border-0 border-solid border-black py-4 text-grey-600 data-[state=active]:border-b data-[state=active]:text-grey-800 data-[state=active]:shadow-none'
                  >
                    <FormattedMessage
                      id='ui.quoteSection.content.tab.planning'
                      defaultMessage='Planning détaillé'
                    />
                  </TabsTrigger>
                )}
              </TabsList>

              <div className='mt-4 overflow-y-scroll p-4 pt-0'>
                <TabsContent value='summary' className='m-0'>
                  <QuoteSectionBodyTableSummary
                    clientPricingItems={clientPricingItemsToDisplay}
                    tableRowActions={tableRowActions}
                    ownerActions={ownerActions}
                    clientProposalId={clientProposal._id}
                    isDragDisabled={!canUpdateQuote}
                  />
                  <QuoteSectionBodyTotalPrice price={price} />
                </TabsContent>
                {displayPlanningTab && (
                  <TabsContent value='planning' className='m-0'>
                    <QuoteSectionBodyTablePlanning
                      planningItemSections={planningItems}
                    />
                  </TabsContent>
                )}
              </div>
            </Tabs>
          ) : (
            emptyStateData && <QuoteEmptyState {...emptyStateData} />
          )}
        </CardContent>

        {displayQuote && (
          <CardFooter className='flex h-20 gap-4 border-t border-t-grey-400 p-[16px]'>
            {shouldDisplayPdfDropdown ? (
              <div className='flex-1'>
                <DownloadPdfDropdown
                  clientRequestId={clientRequest._id}
                  clientProposalId={clientProposal._id}
                  houseId={clientProposal.house?._id}
                />
              </div>
            ) : null}

            {adjustmentPricingItems && adjustmentPricingItems.length > 0 && (
              <Toggle
                nameLeft={
                  <FormattedMessage
                    id='ui.QuoteSection.summary'
                    defaultMessage='Synthèse'
                  />
                }
                nameRight={
                  <FormattedMessage
                    id='ui.QuoteSection.adjustments'
                    defaultMessage='Ajustements'
                  />
                }
                value={isSynthesis}
                setValue={setIsSynthesis}
              />
            )}
            <UpdateQuoteTotalPrice
              priceWithoutVat={
                price?.totalUser?.priceWithDiscount?.priceWithoutVat ?? 0
              }
              priceWithVat={
                price?.totalUser?.priceWithDiscount?.priceWithVat ?? 0
              }
            />
          </CardFooter>
        )}

        {isOpenCreateOrUpdateQuoteProvisionModal && (
          <CreateOrUpdateQuoteProvisionModal
            ownerFeesRate={ownerFeesRate}
            isOpen={isOpenCreateOrUpdateQuoteProvisionModal}
            setIsOpen={setIsOpenCreateOrUpdateQuoteProvisionModal}
            currencyArea={currencyArea}
            clientProposalId={clientProposal._id}
            ownerActions={ownerActions}
            defaultValues={selectedClientPricingItem}
          />
        )}
        {isOpenLoadQuotation && (
          <UploadQuotationModal
            isOpenModal={isOpenLoadQuotation}
            setIsOpenModal={setIsOpenLoadQuotation}
            isModalQuotation={true}
            uploadQuotationData={uploadQuotationData}
            clientProposal={clientProposal}
            ownerUploadedQuotation={ownerUploadedQuotation}
          />
        )}
      </Card>
    </VatStatusProvider>
  )
}

export default QuoteSection
